/**
 * Rechnungsliste (rein, getestet): Rechnungen aus fibu_invoices plus
 * Auftrags-Entwürfe (Lieferschein gedruckt, noch keine Rechnung).
 */

export const STATUS_TABS = [
  { key: 'alle', label: 'Alle' },
  { key: 'entwurf', label: 'Entwurf' },
  { key: 'offen', label: 'Offen' },
  { key: 'faellig', label: 'Fällig' },
  { key: 'teilbezahlt', label: 'Teilbezahlt' },
  { key: 'bezahlt', label: 'Bezahlt' },
  { key: 'storniert', label: 'Storniert' },
];

/** Status einer festgeschriebenen Rechnung (Überfällig wird abgeleitet) */
export function rowStatus(inv, openItem, today) {
  if (inv.status === 'cancelled') return 'storniert';
  if (inv.status === 'draft') return 'entwurf';
  if (inv.status === 'paid') return 'bezahlt';
  const overdue = openItem?.due_date && openItem.due_date < today;
  if (overdue) return 'faellig';
  return inv.status === 'partial' ? 'teilbezahlt' : 'offen';
}

const isAngebot = (o) => String(o.order_type || '').toLowerCase().includes('angebot');

/**
 * @returns {Array<{ key, kind: 'invoice'|'order', inv?, order?, openItem?, status, number, customer, date, net, total, open }>}
 */
export function buildRechnungRows({ orders = [], invoices = [], openItems = [], today }) {
  const oiById = new Map(openItems.map((i) => [i.id, i]));
  const orderById = new Map(orders.map((o) => [o.id, o]));
  const invoicedOrders = new Set(
    invoices.filter((i) => i.kind === 'invoice' && i.order_id && i.status !== 'cancelled').map((i) => i.order_id),
  );
  const cancelledOrders = new Set(
    invoices.filter((i) => i.kind === 'invoice' && i.order_id && i.status === 'cancelled').map((i) => i.order_id),
  );

  const invoiceRows = invoices
    .filter((i) => i.kind === 'invoice')
    .map((inv) => {
      const openItem = inv.open_item_id ? oiById.get(inv.open_item_id) : null;
      const order = inv.order_id ? orderById.get(inv.order_id) : null;
      const status = rowStatus(inv, openItem, today);
      return {
        key: `inv:${inv.id}`,
        kind: 'invoice',
        inv,
        order,
        openItem,
        status,
        number: inv.invoice_no,
        orderNumber: order?.order_number || null,
        customer: inv.customer_name || order?.lead_name || '',
        date: (inv.invoice_date || inv.created_at || '').slice(0, 10),
        net: Number(inv.net_total) || 0,
        total: Number(inv.gross_total) || 0,
        open: ['bezahlt', 'storniert', 'entwurf'].includes(status) ? 0 : Number(openItem?.open_amount ?? inv.gross_total) || 0,
        channel: (order?.source || (inv.source === 'manual' ? 'manuell' : 'agent')).toLowerCase(),
      };
    });

  const draftRows = orders
    .filter((o) => !isAngebot(o) && o.delivery_note_number && o.status !== 'Storniert'
      && !o.invoice_storniert_at && !invoicedOrders.has(o.id) && !cancelledOrders.has(o.id))
    .map((o) => {
      const total = Number(o.total ?? o.gesamtsumme) || Number(o.subtotal) || 0;
      return {
        key: `order:${o.id}`,
        kind: 'order',
        order: o,
        status: 'entwurf',
        number: o.invoice_number || null,
        orderNumber: o.order_number,
        customer: o.lead_name || '',
        date: (o.delivery_note_at || o.created_date || o.created_at || '').slice(0, 10),
        net: Number(o.subtotal) || +(total / 1.19).toFixed(2),
        total,
        open: 0,
        channel: (o.source || 'agent').toLowerCase(),
      };
    });

  return [...invoiceRows, ...draftRows].sort((a, b) => (b.date || '').localeCompare(a.date || ''));
}

export function tabStats(rows) {
  const stats = Object.fromEntries(STATUS_TABS.map((t) => [t.key, { count: 0, sum: 0 }]));
  rows.forEach((r) => {
    stats.alle.count++;
    stats.alle.sum += r.total;
    stats[r.status].count++;
    stats[r.status].sum += ['offen', 'faellig', 'teilbezahlt'].includes(r.status) ? r.open : r.total;
  });
  return stats;
}
